"use client"

import { useMemo, useState } from "react"
import { format } from "date-fns"
import { AlertTriangle, Copy } from "lucide-react"
import type { GanttTask } from "../gantt-chart/data/types"
import type { UseGanttData } from "./useGanttData"
import { useCompanyMembers } from "./useCompanyMembers"
import { toDate, STATUS_LABEL, STATUS_TAG_VALUE, getScheduleHealthFlag, SCHEDULE_HEALTH_LABEL } from "./gantt-utils"
import { GenericTable, type GenericTableColumn } from "@/styles/GenericTable"
import { GenericTag } from "@/styles/GenericTag"
import { StandardButton, DeleteRecordButton } from "@/styles/StandardButtons"
import { AddTaskButton } from "./AddTaskButton"
import { GanttTaskPanel } from "./Gantt"

/**
 * Gantt List View (spec #3) -- every Schedule Item in a table, reusing
 * GenericTable (checkboxes, sort, pagination all built in) instead of a
 * custom table, and the same useGanttData instance Gantt's timeline
 * uses, so an edit here is instantly reflected there. `search` is lifted
 * to scheduling-page.tsx (shared with Gantt -- same Search control, same
 * relative position next to the 3-dot menu in both views).
 */
export function GanttListView({ companyId, userId, projectId, ganttData, search = "" }: { companyId?: string | null; userId?: string; projectId?: string; ganttData: UseGanttData; search?: string }) {
  const { tasks, phases, phaseName, updateTask, addTask, bulkDeleteTasks, bulkCopyTasks, bulkMoveToPhase, setBaseline } = ganttData
  const members = useCompanyMembers(companyId)
  const memberName = (id?: string | null) => members.find((m) => m.id === id)?.fullName ?? "—"

  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  // the task whose details panel is open (clicking a row) -- the same panel the Gantt timeline opens
  const [openTaskId, setOpenTaskId] = useState<string | null>(null)
  const openTask = openTaskId ? ganttData.tasksById.get(openTaskId) ?? null : null

  const items = useMemo(() => {
    return tasks
      .filter((t) => t.type === "task")
      .filter((t) => (statusFilter === "all" ? true : t.status === statusFilter))
      .filter((t) => (search ? t.name.toLowerCase().includes(search.toLowerCase()) : true))
  }, [tasks, statusFilter, search])

  const columns: GenericTableColumn<GanttTask>[] = [
    {
      key: "name",
      header: "Item",
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-1.5">
          <span className="truncate">{row.name}</span>
          {getScheduleHealthFlag(row) && (
            <span title={SCHEDULE_HEALTH_LABEL[getScheduleHealthFlag(row)!]}>
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-red-500" />
            </span>
          )}
        </div>
      ),
    },
    { key: "phase", header: "Phase", sortable: true, sortValue: (row) => phaseName(row.parentId), render: (row) => phaseName(row.parentId) },
    { key: "startDate", header: "Start", sortable: true, render: (row) => format(toDate(row.startDate), "MMM d, yyyy") },
    { key: "endDate", header: "End", sortable: true, render: (row) => format(toDate(row.endDate), "MMM d, yyyy") },
    { key: "duration", header: "Duration", align: "right", sortable: true, render: (row) => `${row.duration}d` },
    {
      key: "progress",
      header: "Progress",
      align: "right",
      sortable: true,
      render: (row) => (
        <select
          value={row.progress}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => updateTask(row.id, { progress: parseInt(e.target.value) })}
          className="w-16 rounded border border-gray-200 px-1 py-0.5 text-xs text-gray-600 focus:outline-none"
        >
          {[0, 10, 25, 50, 75, 90, 100].map((p) => (
            <option key={p} value={p}>
              {p}%
            </option>
          ))}
        </select>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <GenericTag type="status" value={STATUS_TAG_VALUE[row.status]} />
          <select
            value={row.status}
            onChange={(e) => updateTask(row.id, { status: e.target.value as GanttTask["status"] })}
            className="rounded border-none bg-transparent p-0 text-[10px] text-gray-400 focus:outline-none"
            aria-label="Change status"
          >
            {(Object.keys(STATUS_LABEL) as GanttTask["status"][]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </div>
      ),
    },
    { key: "assignee", header: "Assignee", render: (row) => memberName(row.assigneeId) },
  ]

  return (
    <div className="relative flex h-full min-h-0 flex-col rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex flex-shrink-0 flex-wrap items-center gap-2">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 focus:outline-none">
          <option value="all">All Statuses</option>
          {(Object.keys(STATUS_LABEL) as GanttTask["status"][]).map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <AddTaskButton phases={phases} onAddTask={addTask} className="h-10 px-3 text-sm" />

        {selectedKeys.length > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500">{selectedKeys.length} selected</span>
            <StandardButton
              customVariant="white"
              className="h-9 px-2.5 text-xs"
              onClick={() => {
                bulkCopyTasks(selectedKeys)
                setSelectedKeys([])
              }}
            >
              <Copy className="mr-1.5 h-3.5 w-3.5" />
              Copy
            </StandardButton>
            {phases.length > 0 && (
              <select
                defaultValue=""
                onChange={(e) => {
                  if (e.target.value) {
                    bulkMoveToPhase(selectedKeys, e.target.value)
                    setSelectedKeys([])
                  }
                  e.currentTarget.value = ""
                }}
                className="h-9 rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-600 focus:outline-none"
              >
                <option value="" disabled>
                  Move to Phase…
                </option>
                {phases.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            )}
            <StandardButton
              customVariant="white"
              className="h-9 px-2.5 text-xs"
              onClick={() => {
                setBaseline(selectedKeys)
                setSelectedKeys([])
              }}
            >
              Set Baseline
            </StandardButton>
            <DeleteRecordButton
              label="Delete"
              className="h-9 px-2.5 py-0 text-xs"
              onClick={() => {
                if (!window.confirm(`Are you sure you want to delete ${selectedKeys.length} task(s)? This action cannot be undone.`)) return
                bulkDeleteTasks(selectedKeys)
                setSelectedKeys([])
              }}
            />
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <GenericTable
          columns={columns}
          data={items}
          rowKey={(row) => row.id}
          selectable
          selectedKeys={selectedKeys}
          onSelectionChange={setSelectedKeys}
          onRowClick={(row) => setOpenTaskId(row.id)}
          emptyState={<div className="py-10 text-center text-sm text-gray-400">No schedule items yet.</div>}
        />
      </div>

      {openTask && (
        <GanttTaskPanel
          task={openTask}
          ganttData={ganttData}
          companyId={companyId}
          userId={userId}
          projectId={projectId}
          onClose={() => setOpenTaskId(null)}
          onSelectTask={setOpenTaskId}
        />
      )}
    </div>
  )
}
